import { useToast, Toaster, Input, Button } from '@quadminds/mindkit';
import { MindyDocRoles, MindyKnowledgePermissions, MindyStandardRoles } from '@quadminds/mindy-shared';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import type React from 'react';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebounceCallback } from 'usehooks-ts';
import { useIntegrationContext } from '../../../../providers/Integration/Integration.provider';
import { useKnowledgeContext } from '../../../../providers/Knowledge/Knowledge.provider';
import type { FileStatus, FileItem } from '../../../../types/File/File.types';
import { FileBreadcrumb } from '../Breadcrumb/Breadcrumb';
import { DeleteConfirmationDialog } from '../Dialogs/Delete/Delete';
import { MoveDialog } from '../Dialogs/Move/Move';
import { CreateFolder } from '../Dialogs/NewFolder/NewFolder';
import { RenameDialog } from '../Dialogs/Rename/Rename';
import { FileFilters } from '../Filters/Filters';
import MainView from '../MainView/MainView';
import { FilePreviewModal } from '../Modals/Detail';
import UploadFileMethod from '../Modals/UploadFileMethod/UploadFileMethod';
import { SelectionActions } from '../SelectionActions/SelectionActions';
import { ViewToggle } from '../ViewToggle/ViewToggle';

export function Knowledge() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const location = useLocation();
    const navigate = useNavigate();

    const ids = location.pathname
        .replace(/^\/knowledge\//, '') // Quitar el prefijo /knowledge/
        .split('/') // Dividir por cada nivel
        .filter(Boolean);

    // Dialog states

    const containerRef = useRef<HTMLDivElement>(null);
    const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState<boolean>(false);
    const [initialSelectedFiles, setInitialSelectedFiles] = useState<File[]>([]);
    // GET REMOTE DATA
    const {
        dialogs,
        pages,
        paths,
        currentPath,
        handleDialogState,
        selectedItemsToDelete,
        isDragging,
        internalDrag,
        previewFile,
        handlePreviewFile,
        clearSelectItemsToDelete,
        clearSelectedItems: _clearSelectedItems,
        handleDrag,
        handleDeleteItems,
        selectedItems,
        selectedItemToRename,
        selectedItemToReplace,
        isLoadingDelete,
        handleSelectAllItems,
        handleSelectItemsToDelete,
        handleSelectMultipleItemToMove,
        clearSelecteItemToRename,
        clearSelectItemToReplace,
        handleUpdateDocument,
        handleActiveMultipleDocuments,
        isLoadingUpdateDocument,
        handleViewType,
        viewType,
        documentFilters,
        handleFilterDocuments,
        clearFilterDocuments,
    } = useKnowledgeContext();

    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']); // Retrieve userInfo from the cache
    const currentUserPermissions = userInfo?.permissions || [];
    const currentUserRoles = new Set(userInfo?.roles || []);
    const { googleIntegration, setGoogleIntegration } = useIntegrationContext();

    const handleRenameConfirm = (newName: string) => {
        const payload: { name: string; folder_id?: string } = { name: newName };
        if (selectedItemToRename?.type === 'folder') {
            payload.folder_id = ids[1];
        }

        if (selectedItemToRename) {
            handleUpdateDocument(payload, selectedItemToRename.id, selectedItemToRename.type);
        }
        handleDrag(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        // Solo activar el indicador de arrastre si son archivos externos
        // (no archivos que estamos moviendo internamente)
        if (!internalDrag && e.dataTransfer.types.includes('Files')) {
            handleDrag(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        // Solo desactivar si el cursor sale completamente del contenedor
        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
            handleDrag(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        handleDrag(false);

        // Check if files are being dropped from the file system
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files);
            setInitialSelectedFiles(files);
            handleDialogState('isUploadModalOpen', true);
        }
    };

    // Añade una nueva función para realizar la eliminación después de la confirmación
    const confirmDelete = () => {
        if (!selectedItemsToDelete || selectedItemsToDelete.length === 0) return;
        handleDeleteItems();
    };

    const handleSelectMultipleToDelete = () => {
        if (selectedItems.length === 0) return;
        handleSelectItemsToDelete(selectedItems);
        handleDialogState('isDeleteDialogOpen', true);
    };

    // Manejar activación/desactivación de elementos seleccionados *
    const handleToggleActiveSelected = (active: boolean) => {
        const selectedItemsToToggleActive = pages
            .filter((item: FileItem) => selectedItems.includes(item.id))
            .filter((item: FileItem) => item.active === !active)
            .map((item: FileItem) => item.id); // Filtrar solo los elementos que están en el estado opuesto al deseado
        toast({
            title: `Solicitud de ${active ? 'activación' : 'desactivación'}`,
            description: `Iniciando solicitud de ${active ? 'activación' : 'desactivación'} de ${
                selectedItemsToToggleActive.length
            } documentos`,
            variant: 'default',
        });
        handleActiveMultipleDocuments({ active }, selectedItemsToToggleActive);
    };

    const handleSearchByName = (value: string) => {
        if (value.length) {
            handleFilterDocuments({ ...documentFilters, name: value });
        }
        if (!value.length && documentFilters && Object.keys(documentFilters).includes('name')) {
            const { name: _name, ...rest } = documentFilters;
            if (Object.keys(rest).length) {
                handleFilterDocuments(rest);
            } else {
                clearFilterDocuments();
            }
        }
    };
    const debouncedSearch = useDebounceCallback(handleSearchByName, 1200);

    const handleSearchByStatus = (value: FileStatus | null) => {
        if (value !== null) {
            handleFilterDocuments({ ...documentFilters, status: value });
        } else if (documentFilters && Object.keys(documentFilters).includes('status')) {
            const { status: _status, ...rest } = documentFilters;
            if (Object.keys(rest).length) {
                handleFilterDocuments(rest);
            } else {
                clearFilterDocuments();
            }
        }
    };

    const handleSearchByActive = (value: boolean | null) => {
        if (value !== null) {
            handleFilterDocuments({ ...documentFilters, active: String(value) });
        } else if (documentFilters && Object.keys(documentFilters).includes('active')) {
            const { active: _active, ...rest } = documentFilters;
            if (Object.keys(rest).length) {
                handleFilterDocuments(rest);
            } else {
                clearFilterDocuments();
            }
        }
    };

    useEffect(() => {
        // handle inital url home path
        if (ids.length < 2 && ids[0] === 'knowledge') {
            if (currentPath === '/') {
                navigate('/chat');
            } else {
                navigate(`/knowledge${currentPath}`);
            }
        }
    }, [currentPath, ids, navigate]);

    const parentId = pages.some((item) => item.type === 'folder')
        ? pages.find((item) => item.type === 'folder')?.parentId || null
        : pages.find((item) => item.type === 'file')?.parentId || null;

    const showCreateActions =
        currentUserPermissions.includes(MindyKnowledgePermissions.CreateKnowledge) ||
        currentUserRoles.has(MindyStandardRoles.MANAGER) ||
        currentUserRoles.has(MindyDocRoles.ADMIN_DOC) ||
        currentUserRoles.has(MindyDocRoles.MANAGER_DOC);

    return (
        <section data-testid="knowledge-section" className="flex flex-col space-y-4 py-4 px-4 lg:px-8 w-full h-full">
            <h1 className="font-poppins font-semibold text-xl">{t('knowledgeManager.header')}</h1>
            <section
                ref={containerRef}
                className={`border rounded-lg shadow-sm `}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                aria-label={t('knowledgeManager.dropZone')}
            >
                <div className="p-4 border-b flex flex-col gap-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <FileBreadcrumb currentPath={paths || []} />
                        <div className="flex max-lg:justify-between items-center gap-4">
                            {showCreateActions && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        data-testid="uploadBtn"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDialogState('isUploadModalOpen', true)}
                                    >
                                        {t('knowledgeManager.uploadBtn')}
                                    </Button>
                                    <Button
                                        data-testid="newFolderBtn"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setIsCreateFolderDialogOpen(true)}
                                    >
                                        {t('knowledgeManager.newFolderBtn')}
                                    </Button>
                                </div>
                            )}
                            <ViewToggle currentView={viewType} onViewChange={handleViewType} />
                        </div>
                    </div>
                    <div className="flex flex-col flex-wrap lg:flex-row gap-2">
                        <div className="relative flex-1 lg:max-w-80 min-w-48">
                            <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="search"
                                placeholder={t('knowledgeManager.searchPlaceholder')}
                                className="pl-8"
                                onChange={(e) => debouncedSearch(e.target.value)}
                                data-testid="search-input"
                            />
                        </div>
                        <FileFilters
                            activeFilter={
                                documentFilters && Object.keys(documentFilters).includes('active')
                                    ? documentFilters['active'] === 'true'
                                    : null
                            }
                            statusFilter={
                                documentFilters && Object.keys(documentFilters).includes('status')
                                    ? (documentFilters['status'] as FileStatus)
                                    : null
                            }
                            onActiveFilterChange={handleSearchByActive}
                            onStatusFilterChange={handleSearchByStatus}
                        />
                    </div>
                    {/* Barra de acciones para elementos seleccionados */}
                    {selectedItems.length > 0 && (
                        <SelectionActions
                            selectedItems={pages.filter((item: FileItem) => selectedItems.includes(item.id))}
                            onClearSelection={() => handleSelectAllItems(false)}
                            onDeleteSelected={handleSelectMultipleToDelete}
                            onMoveSelected={() => {
                                handleSelectMultipleItemToMove(
                                    pages.filter((item: FileItem) => selectedItems.includes(item.id)),
                                );
                                handleDialogState('isMoveDialogOpen', true);
                            }}
                            onToggleActiveSelected={handleToggleActiveSelected}
                        />
                    )}
                </div>

                <div className="min-h-[300px] relative">
                    {isDragging && (
                        <div className="absolute inset-0 bg-primary/10 flex pt-4 justify-center border-2 border-dashed border-primary rounded-md z-10">
                            <div className="text-primary font-medium">{t('knowledgeManager.uploadDropZone')}</div>
                        </div>
                    )}
                    <MainView />
                </div>

                <RenameDialog
                    isOpen={dialogs.isRenameDialogOpen}
                    onClose={() => {
                        handleDialogState('isRenameDialogOpen', false);
                        clearSelecteItemToRename();
                    }}
                    onConfirm={handleRenameConfirm}
                    item={selectedItemToRename}
                    isLoading={isLoadingUpdateDocument}
                />
                <MoveDialog isOpen={dialogs.isMoveDialogOpen} currentFolderId={ids[1] || ''} parentId={parentId} />
                <FilePreviewModal
                    isOpen={dialogs.isPreviewModalOpen}
                    onClose={() => handleDialogState('isPreviewModalOpen', false)}
                    file={previewFile}
                    onAddComment={() => null}
                    userInfo={userInfo}
                />

                <DeleteConfirmationDialog
                    isLoding={isLoadingDelete}
                    isOpen={dialogs.isDeleteDialogOpen}
                    onClose={() => {
                        handleDialogState('isDeleteDialogOpen', false);
                        clearSelectItemsToDelete();
                    }}
                    onConfirm={confirmDelete}
                    items={pages.filter((item: FileItem) => selectedItemsToDelete.includes(item.id))}
                />
                <CreateFolder
                    isOpen={isCreateFolderDialogOpen}
                    onOpenChange={setIsCreateFolderDialogOpen}
                    onClose={() => setIsCreateFolderDialogOpen(false)}
                />
                <UploadFileMethod
                    isOpen={
                        dialogs.isUploadModalOpen ||
                        googleIntegration.selfOpenedGooglePicker ||
                        dialogs.isReplaceDialogOpen
                    }
                    dialogs={dialogs}
                    handleDialogState={(state) => handleDialogState('isUploadModalOpen', state)}
                    onClose={() => {
                        handleDialogState('isUploadModalOpen', false);
                        handleDialogState('isReplaceDialogOpen', false);
                        handlePreviewFile(null);
                        setInitialSelectedFiles([]);
                        clearSelectItemToReplace();
                        setGoogleIntegration({ ...googleIntegration, selfOpenedGooglePicker: false });
                        handleDrag(false);
                    }}
                    initialSelectedFiles={initialSelectedFiles}
                />
                <Toaster />
            </section>
        </section>
    );
}

export default Knowledge;
